import React, { useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { createPortal } from "react-dom";

const NmCompletedHelpDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((s) => !s);
  const drawerContentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (drawerContentRef.current && !drawerContentRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const drawer = (
    <div className="drawer-portal">
      <Drawer
        open={isOpen}
        onClose={toggle}
        direction="right"
        size={500}
        duration={400}
        overlayOpacity={0.5}
        overlayColor="#000"
        customIdSuffix="help-guide-nm-completed"
        className="help-guide-drawer"
        style={{ top: "73px", height: "calc(100vh - 73px)", transition: "all 0.4s ease-in-out" }}
      >
        <div className="drawer-content" ref={drawerContentRef}>
          <div className="drawer-header">
            <div className="header-title">
              <span className="emoji">🟢</span>
              <span className="title">Near Miss – Completed Tab Help</span>
            </div>
            <button className="close-button" onClick={toggle} aria-label="Close help panel">
              &times;
            </button>
          </div>

          <div className="drawer-body">
  <p>
    The Completed Near Miss (NM) Detail Page provides a full record of a Near Miss where the
    investigation is closed. It serves as the final documented output of the investigation process,
    including analysis findings and CAPA recommendations.
  </p>

  <hr />

  <h5 className="section-title">Record Overview</h5>
  <p>At the top of the page, you will see:</p>
  <ul className="steps-list">
    <li>• Record Number – e.g., 940</li>
    <li>• NM Type – UA / UC (Unsafe Act / Unsafe Condition)</li>
    <li>• Activity Name – e.g., Bar Bending</li>
    <li>• NM Reported Month – e.g., July</li>
  </ul>
  <p>Below this, the timeline displays:</p>
  <ul className="steps-list">
    <li>• Reported details</li>
    <li>• Incident date</li>
    <li>• Involved person names</li>
    <li>• Investigation team member names</li>
    <li>• Discussion comments (if any)</li>
  </ul>

  <hr />

  <h5 className="section-title">Reported Details Section</h5>
  <p>This section captures the original NM report:</p>
  <ul className="steps-list">
    <li>• Evidence – uploaded images.</li>
    <li>• Incident Description – description of the NM event.</li>
    <li>• Hazard Type – type of hazard involved.</li>
    <li>• Risk Level – assessed severity of the NM.</li>
  </ul>

  <hr />

  <h5 className="section-title">Activity Information Section</h5>
  <p>Here you will find:</p>
  <ul className="steps-list">
    <li>• Service and its linked Plant/Project/Client.</li>
    <li>• Department/Contractor/Location where the NM was reported.</li>
  </ul>

  <hr />

  <h5 className="section-title">Incident Details Section</h5>
  <p>Provides factual information:</p>
  <ul className="steps-list">
    <li>• Incident Date – when the NM occurred.</li>
    <li>• Incident Time – exact occurrence time.</li>
    <li>• Involved Person Details – names of those involved.</li>
  </ul>

  <hr />

  <h5 className="section-title">NM Investigation Start Detail</h5>
  <ul className="steps-list">
    <li>• Shows the date when the investigation officially began.</li>
    <li>• This is when the record moved from Pending → In-progress.</li>
  </ul>

  <hr />

  <h5 className="section-title">NM Investigation Target & Team Details</h5>
  <ul className="steps-list">
    <li>• Displays the investigation target date – the agreed timeline for completion.</li>
    <li>• Lists investigation team members with the crown icon identifying the Team Lead.</li>
  </ul>

  <hr />

  <h5 className="section-title">NM Investigation Analysis – Completed Findings</h5>
  <p><b>Step 1 – Incident Analysis Question (5Y Analysis)</b></p>
  <ul className="steps-list">
    <li>• Displays the 5-Why analysis questions and responses created by the investigation team.</li>
    <li>• Shows documents uploaded by the team as part of evidence or supporting investigation materials.</li>
  </ul>

  <p><b>Step 2 – Root Cause Analysis</b></p>
  <ul className="steps-list">
    <li>• Displays finalized Contributing Factors, Immediate Factors, and Root Causes.</li>
    <li>• Root causes are visualized in the Fishbone Diagram for clarity.</li>
  </ul>

  <p><b>Accident Costing</b></p>
  <ul className="steps-list">
    <li>• Displays NM-related cost data captured under defined costing heads.</li>
    <li>• Helps quantify the financial impact of the NM.</li>
  </ul>

  <p><b>Step 3 – CAPA (Corrective & Preventive Actions)</b></p>
  <ul className="steps-list">
    <li>• Displays the list of Final CAPAs approved by the Team Lead for implementation.</li>
    <li>• CAPAs are already frozen and moved to the CAPA In-progress tab for assigned users to act upon.</li>
  </ul>

  <hr />

  <h5 className="section-title">Discussion Section</h5>
  <p>A record of investigation-related discussions between team members.</p>
  <ul className="steps-list">
    <li>• Documents team collaboration, ideas, and preventive strategies discussed during the investigation.</li>
    <li>• Ensures transparency and a learning record for future reference.</li>
  </ul>

  <hr />

  <h5 className="section-title">UAUC Detail Analysis (Right-hand Panel)</h5>
  <p>The right-hand side provides post-investigation insights based on the activity:</p>
  <ul className="steps-list">
    <li>• IRA Analysis – overall accident statistics.</li>
    <li>• Near Miss Analysis – reporting patterns.</li>
    <li>• UA vs UC Analysis – comparison of unsafe acts vs unsafe conditions.</li>
    <li>• Risk-Based Analysis – distribution of risk levels.</li>
    <li>• Good Act & TBT Count – positive actions and toolbox talks.</li>
    <li>• Internal & External Training Count – trainings conducted.</li>
    <li>• Employee & Contractor Training Count – training coverage for both groups.</li>
  </ul>
  <p>
    This data supports continuous improvement and strategy building to reduce unsafe
    acts/conditions.
  </p>

  <hr />

  <div className="alert alert-info">
    <b>✅ Note:</b> Once the Near Miss is in Completed status, all investigation findings are frozen.
    CAPAs are handed over for implementation and tracked separately in the CAPA In-progress tab.
  </div>
</div>

        </div>
      </Drawer>
    </div>
  );

  return (
    <>
      <div
        className="toggle-help-btn"
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
        style={{ fontSize: "16px", cursor: "pointer" }}
      >
        Page Help
        <img src="/images/formkit_help.svg" alt="" />
      </div>
      {createPortal(drawer, document.body)}
    </>
  );
};

export default NmCompletedHelpDrawer;
